import React, { useState } from 'react'
import { GitBranch, ChevronDown, ChevronRight, Play } from 'lucide-react'

const RULES = [
  // Graph Classification
  { id: 'R001', category: 'graph_classification', name: '증가 그래프 분류', condition: 'slopeValue > 0', conclusion: 'Graph → IncreasingGraph', example: 'y = 2x+1 → 증가 그래프', severity: null },
  { id: 'R002', category: 'graph_classification', name: '감소 그래프 분류', condition: 'slopeValue < 0', conclusion: 'Graph → DecreasingGraph', example: 'y = -x+3 → 감소 그래프', severity: null },
  { id: 'R003', category: 'graph_classification', name: '수평선 분류', condition: 'slopeValue = 0', conclusion: 'Graph → HorizontalGraph', example: 'y = 5 → 수평선', severity: null },
  // Function Classification
  { id: 'R004', category: 'function_classification', name: '정비례 함수 분류', condition: 'interceptValue = 0 ∧ slopeValue ≠ 0', conclusion: 'LinearFunction → ProportionalFunction', example: 'y = 3x → 정비례', severity: null },
  { id: 'R005', category: 'function_classification', name: '일반 1차 함수 분류', condition: 'interceptValue ≠ 0 ∧ slopeValue ≠ 0', conclusion: 'LinearFunction (일반형)', example: 'y = 2x+1 → 일반 1차 함수', severity: null },
  // Misconception Detection
  { id: 'R006', category: 'misconception_detection', name: 'M03: 기울기·y절편 혼동', condition: 'slope_as_intercept = true ∨ intercept_as_slope = true', conclusion: 'StudentAnswer → triggers M03', example: 'y=2x+5에서 기울기=5라고 답', severity: 'high' },
  { id: 'R007', category: 'misconception_detection', name: 'M01: 기울기 단순 수 오해', condition: 'slope_understood_as_number_only = true', conclusion: 'StudentAnswer → triggers M01', example: '기울기=2, 방향 무시', severity: 'high' },
  { id: 'R008', category: 'misconception_detection', name: 'M02: x/y 역할 혼동', condition: 'xy_swapped = true', conclusion: 'StudentAnswer → triggers M02', example: '(3,5)를 x=5, y=3으로 읽음', severity: 'high' },
  { id: 'R009', category: 'misconception_detection', name: 'M04: 정비례≠1차 함수 혼동', condition: 'proportional_equals_linear = false ∧ student says false', conclusion: 'StudentAnswer → triggers M04', example: 'y=3x는 1차함수 아니라고 답', severity: 'medium' },
  { id: 'R010', category: 'misconception_detection', name: 'M05: 음수 기울기=함수 아님', condition: 'negative_slope_not_function = true', conclusion: 'StudentAnswer → triggers M05', example: 'y=-x+3은 함수 아니라고 답', severity: 'medium' },
  { id: 'R011', category: 'misconception_detection', name: 'M06: 표에서 변화율 오류', condition: 'table_rate_wrong = true', conclusion: 'StudentAnswer → triggers M06', example: '변화율 계산시 분모·분자 뒤집음', severity: 'medium' },
  { id: 'R012', category: 'misconception_detection', name: 'M07: 그래프↔식 변환 오류', condition: 'graph_interpretation_wrong = true', conclusion: 'StudentAnswer → triggers M07', example: '그래프에서 기울기를 잘못 읽음', severity: 'low' },
  { id: 'R013', category: 'misconception_detection', name: 'M08: 문장제→식 변환 오류', condition: 'word_problem_wrong = true', conclusion: 'StudentAnswer → triggers M08', example: '기본요금을 기울기로 착각', severity: 'low' },
  // Feedback Generation
  { id: 'R014', category: 'feedback_generation', name: '오개념 피드백 생성', condition: 'StudentAnswer → triggers Misconception', conclusion: 'Misconception → recommends Feedback', example: 'M03 감지 → F-M03 피드백 제공', severity: null },
  { id: 'R015', category: 'feedback_generation', name: '격려 피드백', condition: 'score ≥ 80 ∧ misconceptions = 0', conclusion: 'Feedback (encouragement type)', example: '100점 → 훌륭해요! 피드백', severity: null },
  // Learning Path
  { id: 'R016', category: 'learning_path', name: '다음 단계 진행', condition: 'mastery = true ∧ score ≥ 80', conclusion: 'recommend advance to next LearningStep', example: 'LS05 마스터 → LS06 진행', severity: null },
  { id: 'R017', category: 'learning_path', name: '보강 학습 권장', condition: '60 ≤ score < 80 ∨ misconceptions ≤ 1', conclusion: 'recommend reinforce current step', example: '65점 → 현재 단계 보강', severity: null },
  { id: 'R018', category: 'learning_path', name: '교정 학습 권장', condition: 'score < 60 ∨ misconceptions > 1', conclusion: 'recommend remediate (prior step)', example: '40점 + M03,M04 → 이전 단계 복습', severity: null },
]

const CATEGORIES: Record<string, { label: string; color: string }> = {
  graph_classification: { label: '그래프 분류', color: 'bg-emerald-100 text-emerald-700' },
  function_classification: { label: '함수 분류', color: 'bg-blue-100 text-blue-700' },
  misconception_detection: { label: '오개념 감지', color: 'bg-red-100 text-red-700' },
  feedback_generation: { label: '피드백 생성', color: 'bg-amber-100 text-amber-700' },
  learning_path: { label: '학습 경로', color: 'bg-purple-100 text-purple-700' },
}

const SEVERITY_COLOR: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-orange-100 text-orange-700',
  low: 'bg-yellow-100 text-yellow-700',
}

export default function RulesPage() {
  const [selectedCat, setSelectedCat] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const filtered = RULES.filter(r => {
    const matchCat = !selectedCat || r.category === selectedCat
    const matchSearch = !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.id.toLowerCase().includes(search.toLowerCase())
    return matchCat && matchSearch
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <GitBranch size={22} className="text-red-600" />
        <div>
          <h1 className="text-xl font-bold text-slate-800">규칙 엔진 — SWRL 규칙</h1>
          <p className="text-sm text-slate-500">18개 추론 규칙 · 6개 범주 · Agentic AI 의사결정 기반</p>
        </div>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCat(null)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!selectedCat ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
        >
          전체 ({RULES.length})
        </button>
        {Object.entries(CATEGORIES).map(([key, { label, color }]) => (
          <button
            key={key}
            onClick={() => setSelectedCat(selectedCat === key ? null : key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedCat === key ? color : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            {label} ({RULES.filter(r => r.category === key).length})
          </button>
        ))}
      </div>

      <input
        type="text"
        placeholder="규칙 검색..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="input w-full"
      />

      {/* Rules list */}
      <div className="space-y-2">
        {filtered.map(rule => (
          <div key={rule.id} className="card border border-slate-200">
            <button
              className="w-full flex items-center gap-3 text-left"
              onClick={() => setOpenId(openId === rule.id ? null : rule.id)}
            >
              <span className="text-xs font-mono font-bold text-slate-400 w-10 flex-shrink-0">{rule.id}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-800">{rule.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORIES[rule.category].color}`}>
                    {CATEGORIES[rule.category].label}
                  </span>
                  {rule.severity && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SEVERITY_COLOR[rule.severity]}`}>
                      심각도: {rule.severity === 'high' ? '높음' : rule.severity === 'medium' ? '중간' : '낮음'}
                    </span>
                  )}
                </div>
              </div>
              {openId === rule.id ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
            </button>

            {openId === rule.id && (
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-xs font-semibold text-slate-400 w-14 flex-shrink-0 mt-0.5">조건</span>
                  <code className="text-xs bg-blue-50 text-blue-800 rounded px-2 py-1 font-mono leading-relaxed flex-1">{rule.condition}</code>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-xs font-semibold text-slate-400 w-14 flex-shrink-0 mt-0.5">결론</span>
                  <code className="text-xs bg-emerald-50 text-emerald-800 rounded px-2 py-1 font-mono leading-relaxed flex-1">{rule.conclusion}</code>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-xs font-semibold text-slate-400 w-14 flex-shrink-0 mt-0.5">예시</span>
                  <span className="text-xs text-slate-600 bg-slate-50 rounded px-2 py-1 flex-1">{rule.example}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Decision Flow */}
      <div className="card bg-gradient-to-br from-slate-50 to-blue-50">
        <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <Play size={14} className="text-primary-500" />
          Agentic AI 의사결정 흐름
        </h2>
        <div className="flex flex-wrap gap-2 items-center justify-center text-xs">
          {[
            '① 답안 수신', '② 정답 확인', '③ 오개념 감지 (R006~R013)',
            '④ 우선순위 결정', '⑤ 피드백 생성 (R014~R015)',
            '⑥ 다음 단계 추천 (R016~R018)', '⑦ 단원 완료 확인'
          ].map((step, i, arr) => (
            <React.Fragment key={step}>
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 font-medium text-slate-700 shadow-sm">
                {step}
              </div>
              {i < arr.length - 1 && <ChevronRight size={14} className="text-slate-400" />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}
